import { useState, type ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ArrowRightLeft, ArrowUpDown, FolderOpen, Maximize2, Minimize2 } from 'lucide-react'
import { create } from '@bufbuild/protobuf'
import { Skeleton } from 'skullet/react'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { InquiryAssigneeDropdown } from '@/components/leads'
import { badgeVariantToTailwind } from '@/components/leads/stage-colors'
import { PipelineInvoicePopoverCell } from '@/components/pipeline/pipeline-invoice-popover-cell'
import { PipelineEventsPopoverCell } from '@/components/pipeline/pipeline-events-popover-cell'
import { formatCurrencyWhole } from '@/lib/currency-utils'
import { formatTimestamp, formatTimestampRelativeLong, formatTimestampWithTime } from '@/lib/date-utils'
import type { BadgeVariant } from '@/lib/enum-utils'
import { cn } from '@/lib/utils'
import type { OrganizationUser } from '@/gen/cortado/v1/organizations_pb'
import {
  JobBoardStageEnum,
  JobSortDirectionEnum,
  JobSortFieldEnum,
  type InquiryStageEnum,
} from '@/gen/cortado/v1/settings_pb'
import { JobLiteSchema, type JobLite, type PipelineStageBucket } from '@/gen/cortado/v1/jobs_pb'
import { useInfinitePipelineBoardColumn, useMoveLeadStage, type PipelineFilterOptions } from '@/stores/pipeline.store'
import {
  PIPELINE_BOARD_STAGE_TILE,
  PIPELINE_BOARD_STAGES,
  PIPELINE_LEAD_DRAG_STAGES,
  pipelineEventDateRange,
  pipelineEventsMeta,
  pipelineJobCustomerName,
  pipelineLeadDropStage,
  pipelineOtherLeadStage,
  pipelineOwnerInitials,
  SAMPLE_PIPELINE_JOBS,
  type PipelineSort,
  type PipelineTileKey,
} from '@/components/pipeline/pipeline-job-view'

/** The dragged card's inquiry id + originating board-stage column. */
interface PipelineLeadDragState {
  inquiryId: string
  boardStage: JobBoardStageEnum
}

/**
 * Column titles and status dots, in the same plain "New" / "In Progress"
 * style as the Leads kanban headers.
 */
export const PIPELINE_BOARD_COLUMN: Record<JobBoardStageEnum, { label: string; dot: BadgeVariant }> = {
  [JobBoardStageEnum.JOB_BOARD_STAGE_UNSPECIFIED]: { label: '', dot: 'secondary' },
  [JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_NEW]: { label: 'New', dot: 'info' },
  [JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_IN_PROGRESS]: { label: 'In Progress', dot: 'warning' },
  [JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_SENT]: { label: 'Quote Sent', dot: 'purple' },
  [JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_DECLINED]: { label: 'Declined', dot: 'danger' },
  [JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_NOT_INVOICED]: { label: 'Not Invoiced', dot: 'orange' },
  [JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_PENDING_DEPOSIT]: { label: 'Pending Deposit', dot: 'pink' },
  [JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_CONFIRMED]: { label: 'Confirmed', dot: 'success' },
  [JobBoardStageEnum.JOB_BOARD_STAGE_COMPLETE]: { label: 'Complete', dot: 'secondary' },
}

/** Quote- and booking-phase columns, where the card shows quoted/paid progress. */
const PIPELINE_PAYMENT_STAGES: ReadonlySet<JobBoardStageEnum> = new Set([
  JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_SENT,
  JobBoardStageEnum.JOB_BOARD_STAGE_QUOTE_DECLINED,
  JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_NOT_INVOICED,
  JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_PENDING_DEPOSIT,
  JobBoardStageEnum.JOB_BOARD_STAGE_BOOKING_CONFIRMED,
  JobBoardStageEnum.JOB_BOARD_STAGE_COMPLETE,
])

/** Three sample cards per column, staged into that column so each draws its own card shape. */
const SAMPLE_COLUMN_JOBS = new Map(
  PIPELINE_BOARD_STAGES.map((stage) => [
    stage,
    SAMPLE_PIPELINE_JOBS.slice(0, 3).map((job) => create(JobLiteSchema, { ...job, boardStage: stage })),
  ]),
)

const PIPELINE_BOARD_SORT_OPTIONS: { value: string; label: string; sort: PipelineSort }[] = [
  {
    value: 'default',
    label: 'Default order',
    sort: { field: JobSortFieldEnum.JOB_SORT_FIELD_UNSPECIFIED, direction: JobSortDirectionEnum.JOB_SORT_DIRECTION_UNSPECIFIED },
  },
  {
    value: 'event_asc',
    label: 'Event date (soonest)',
    sort: { field: JobSortFieldEnum.JOB_SORT_FIELD_FIRST_EVENT_DATE, direction: JobSortDirectionEnum.JOB_SORT_DIRECTION_ASC },
  },
  {
    value: 'event_desc',
    label: 'Event date (latest)',
    sort: { field: JobSortFieldEnum.JOB_SORT_FIELD_FIRST_EVENT_DATE, direction: JobSortDirectionEnum.JOB_SORT_DIRECTION_DESC },
  },
  {
    value: 'quoted_desc',
    label: 'Quoted (highest)',
    sort: { field: JobSortFieldEnum.JOB_SORT_FIELD_QUOTED, direction: JobSortDirectionEnum.JOB_SORT_DIRECTION_DESC },
  },
  {
    value: 'updated_desc',
    label: 'Recently updated',
    sort: { field: JobSortFieldEnum.JOB_SORT_FIELD_UPDATED_AT, direction: JobSortDirectionEnum.JOB_SORT_DIRECTION_DESC },
  },
]

/**
 * The select value for the current sort. A table-header sort with no board
 * option (e.g. customer name) reads as "custom" so the select shows it as such
 * instead of pretending the order is the default.
 */
function pipelineBoardSortValue(sort: PipelineSort | undefined): string {
  if (!sort) return 'default'
  const match = PIPELINE_BOARD_SORT_OPTIONS.find(
    (option) => option.sort.field === sort.field && option.sort.direction === sort.direction,
  )
  return match?.value ?? 'custom'
}

interface PipelineBoardProps {
  ownersById: Map<string, OrganizationUser>
  /** When a single phase tile is active, only that phase's board-stage columns render. */
  activeTile: PipelineTileKey
  /** Per-stage totals from the pipeline summary; undefined while it loads. */
  stageBuckets: PipelineStageBucket[] | undefined
  /** Shared toolbar filters (search/owner/event date/sort) — each column adds its own `board_stages`. */
  filterOptions?: Omit<PipelineFilterOptions, 'phases' | 'paymentStates' | 'boardStages'>
  /** Current sort, shared with the table view. The sort select is hidden without `onSortChange`. */
  sort?: PipelineSort
  onSortChange?: (sort: PipelineSort) => void
}

/** Neutral avatar shared with the table view — no per-name color. */
function PipelineAvatar({ initials }: { initials: string }) {
  return (
    <Avatar className="h-6 w-6 text-[10px]">
      <AvatarFallback className="bg-elevated font-semibold text-foreground select-none">{initials}</AvatarFallback>
    </Avatar>
  )
}

export function PipelineBoard({
  ownersById,
  activeTile,
  stageBuckets,
  filterOptions,
  sort,
  onSortChange,
}: PipelineBoardProps) {
  const stages =
    activeTile === 'all' || activeTile === 'due'
      ? PIPELINE_BOARD_STAGES
      : PIPELINE_BOARD_STAGES.filter((stage) => PIPELINE_BOARD_STAGE_TILE[stage] === activeTile)

  const { moveLeadStage } = useMoveLeadStage()
  const [dragState, setDragState] = useState<PipelineLeadDragState | null>(null)
  const [compactView, setCompactView] = useState(false)

  const moveStage = async (inquiryId: string, newStage: InquiryStageEnum) => {
    try {
      await moveLeadStage(inquiryId, newStage)
    } catch (error) {
      console.error('Failed to move lead stage:', error)
      toast.error('Failed to move lead')
    }
  }

  const handleDrop = (targetStage: JobBoardStageEnum) => {
    const dragged = dragState
    setDragState(null)
    if (!dragged) return
    const targetInquiryStage = pipelineLeadDropStage(dragged.boardStage, targetStage)
    if (targetInquiryStage === null) return
    void moveStage(dragged.inquiryId, targetInquiryStage)
  }

  const sortValue = pipelineBoardSortValue(sort)

  return (
    <Card className="flex min-h-[480px] flex-1 flex-col gap-4 p-6">
      <div className="flex flex-shrink-0 items-center justify-end gap-4">
        {onSortChange && (
          <Select
            value={sortValue}
            onValueChange={(value) => {
              const option = PIPELINE_BOARD_SORT_OPTIONS.find((candidate) => candidate.value === value)
              if (option) onSortChange(option.sort)
            }}
          >
            <SelectTrigger size="sm" className="w-[190px]" aria-label="Sort jobs by">
              <ArrowUpDown className="size-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sortValue === 'custom' && (
                <SelectItem value="custom" disabled>
                  Custom (from table)
                </SelectItem>
              )}
              {PIPELINE_BOARD_SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button variant="outline" size="sm" className="h-8" onClick={() => setCompactView((current) => !current)}>
          {compactView ? (
            <>
              <Maximize2 className="size-4" />
              Expand
            </>
          ) : (
            <>
              <Minimize2 className="size-4" />
              Compact
            </>
          )}
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 gap-4 overflow-x-auto overflow-y-hidden pb-4 scrollbar-always-visible">
        {stages.map((stage) => (
          <PipelineBoardColumn
            key={stage}
            stage={stage}
            bucket={stageBuckets && (stageBuckets.find((bucket) => bucket.stage === stage) ?? null)}
            ownersById={ownersById}
            filterOptions={filterOptions}
            compactView={compactView}
            dragState={dragState}
            onDragStart={setDragState}
            onDrop={handleDrop}
            onMoveStage={moveStage}
          />
        ))}
      </div>
    </Card>
  )
}

interface PipelineBoardColumnProps {
  stage: JobBoardStageEnum
  /** Null when the summary has no bucket for this stage; undefined while the summary loads. */
  bucket: PipelineStageBucket | null | undefined
  ownersById: Map<string, OrganizationUser>
  filterOptions?: Omit<PipelineFilterOptions, 'phases' | 'paymentStates' | 'boardStages'>
  compactView: boolean
  dragState: PipelineLeadDragState | null
  onDragStart: (dragState: PipelineLeadDragState | null) => void
  onDrop: (targetStage: JobBoardStageEnum) => void
  onMoveStage: (inquiryId: string, newStage: InquiryStageEnum) => void | Promise<void>
}

function PipelineBoardColumn({
  stage,
  bucket,
  ownersById,
  filterOptions,
  compactView,
  dragState,
  onDragStart,
  onDrop,
  onMoveStage,
}: PipelineBoardColumnProps) {
  const { jobs, totalCount, hasMore, fetchNextPage, isFetchingNextPage, isLoading } = useInfinitePipelineBoardColumn(
    stage,
    filterOptions,
  )

  // Header totals come from GetPipelineSummary's stage_buckets (the true
  // server-side total for this stage), not from this column's own loaded
  // rows — those only back the "Load more" affordance below.
  const count = bucket?.count ?? 0
  const quotedCents = bucket?.quotedCents ?? 0n
  const column = PIPELINE_BOARD_COLUMN[stage]
  const countLabel = jobs.length > 0 && count > jobs.length ? `${jobs.length} of ${count}` : count
  const summaryLoaded = bucket !== undefined

  // Only the two lead columns accept a drop — every other column stays a
  // click-through/navigate-only target.
  const isDropTarget = dragState !== null && pipelineLeadDropStage(dragState.boardStage, stage) !== null

  return (
    <div
      className="flex min-h-0 min-w-[320px] flex-1 flex-col"
      data-testid={`pipeline-board-column-${stage}`}
      onDragOver={isDropTarget ? (event) => event.preventDefault() : undefined}
      onDrop={isDropTarget ? () => onDrop(stage) : undefined}
    >
      <div className="mb-4 flex items-center gap-2">
        <div className={cn('h-2 w-2 rounded-full', badgeVariantToTailwind(column.dot))} />
        <h2 className="text-lg font-semibold text-foreground">{column.label}</h2>
        {summaryLoaded ? (
          <>
            <span className="ml-auto text-xs text-muted-foreground">${formatCurrencyWhole(Number(quotedCents))}</span>
            <Badge variant="secondary">{countLabel}</Badge>
          </>
        ) : (
          <>
            <SkeletonBar className="ml-auto h-3 w-12" />
            <SkeletonBar className="h-5 w-8" />
          </>
        )}
      </div>

      <div
        className={cn(
          'scroll-fade-y flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto rounded-lg bg-muted/50 p-3 transition-colors',
          isDropTarget && 'ring-2 ring-primary/40',
        )}
      >
        <Skeleton
          data={isLoading ? null : jobs}
          sample={SAMPLE_COLUMN_JOBS.get(stage)}
          id={`pipeline:column:${stage}`}
          label={`Loading ${column.label} jobs`}
        >
          {(jobs) => (
            <div className="flex flex-col gap-3">
              {jobs.length === 0 ? (
                <div className="flex h-32 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-border">
                  <p className="text-sm text-muted-foreground">No jobs</p>
                </div>
              ) : (
                jobs.map((job) => (
                  <PipelineBoardCard
                    key={job.id}
                    job={job}
                    owner={job.assignedTo ? ownersById.get(job.assignedTo) : undefined}
                    compactView={compactView}
                    onDragStart={onDragStart}
                    onMoveStage={onMoveStage}
                  />
                ))
              )}
            </div>
          )}
        </Skeleton>
        {!isLoading && hasMore && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full shrink-0"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            Load more ({jobs.length} of {totalCount})
          </Button>
        )}
      </div>
    </div>
  )
}

interface PipelineBoardCardProps {
  job: JobLite
  owner: OrganizationUser | undefined
  compactView: boolean
  onDragStart: (dragState: PipelineLeadDragState | null) => void
  onMoveStage: (inquiryId: string, newStage: InquiryStageEnum) => void | Promise<void>
}

function PipelineBoardCard({ job, owner, compactView, onDragStart, onMoveStage }: PipelineBoardCardProps) {
  const navigate = useNavigate()
  const goToJob = () => navigate({ to: '/pipeline/$id', params: { id: job.id } })
  // Only lead-column cards (New/In Progress) are draggable, and only when the
  // job has an inquiry to move (every lead-phase job does).
  const isLeadCard = PIPELINE_LEAD_DRAG_STAGES.has(job.boardStage) && !!job.inquiryId
  const otherStage = job.inquiryId ? pipelineOtherLeadStage(job.boardStage) : undefined
  const name = pipelineJobCustomerName(job)
  const dateRange = job.firstEventDate ? pipelineEventDateRange(job) : undefined
  const quoted = job.quotedCents != null ? `$${formatCurrencyWhole(Number(job.quotedCents))}` : undefined

  const assignee = job.inquiryId ? (
    <div onClick={(event) => event.stopPropagation()}>
      <InquiryAssigneeDropdown
        inquiryId={job.inquiryId}
        assignedTo={job.assignedTo}
        assignedToUser={owner?.user}
        hideLabel
        className="h-6 w-6 shrink-0 gap-0 rounded-full border-none bg-transparent p-0 hover:bg-transparent"
      />
    </div>
  ) : (
    <PipelineAvatar initials={pipelineOwnerInitials(owner)} />
  )

  const footerStamp = job.quoteAcceptedAt
    ? { label: 'Accepted on ', at: job.quoteAcceptedAt, text: formatTimestamp(job.quoteAcceptedAt) }
    : job.createdAt
      ? { label: 'Received ', at: job.createdAt, text: formatTimestampRelativeLong(job.createdAt) }
      : undefined

  const footerTime = footerStamp ? (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <span className="cursor-help text-xs text-muted-foreground">
          {compactView ? '' : footerStamp.label}
          {footerStamp.text}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom">{formatTimestampWithTime(footerStamp.at)}</TooltipContent>
    </Tooltip>
  ) : null

  const body = compactView ? (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">
            {dateRange ?? 'No date'} - {name}
          </div>
          {job.contactCompanyName && <div className="text-xs text-muted-foreground">{job.contactCompanyName}</div>}
        </div>
        {quoted && (
          <Badge variant="secondary" className="shrink-0">
            {quoted}
          </Badge>
        )}
      </div>
      <div className="flex items-center justify-end gap-2">
        {footerTime}
        {assignee}
      </div>
    </div>
  ) : (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {dateRange && <div className="text-sm font-medium">{dateRange}</div>}
          <div className={dateRange ? 'mt-2' : undefined}>
            <div className="text-sm font-semibold">{name}</div>
            {job.contactCompanyName && <div className="text-xs text-muted-foreground">{job.contactCompanyName}</div>}
          </div>
        </div>
        {quoted && (
          <Badge variant="secondary" className="shrink-0">
            {quoted}
          </Badge>
        )}
      </div>

      <div onClick={(event) => event.stopPropagation()}>
        <PipelineEventsPopoverCell
          job={job}
          triggerClassName="-mx-1 rounded-md px-1 text-left text-xs text-muted-foreground hover:bg-accent"
        >
          {pipelineEventsMeta(job)}
        </PipelineEventsPopoverCell>
      </div>

      {PIPELINE_PAYMENT_STAGES.has(job.boardStage) && (
        <div onClick={(event) => event.stopPropagation()}>
          <PipelineInvoicePopoverCell job={job} />
        </div>
      )}

      <div className="flex items-center justify-between gap-2 border-t pt-1">
        {footerTime}
        <div className="ml-auto flex items-center gap-2">{assignee}</div>
      </div>
    </div>
  )

  return (
    <PipelineBoardCardMenu otherStage={otherStage} onOpen={goToJob} onMove={(stage) => onMoveStage(job.inquiryId!, stage)}>
      <Card
        // A `<div role="button">`-style card, not `<button>`, because it hosts
        // nested interactive controls (assignee picker, quoted/paid popover).
        role="button"
        tabIndex={0}
        draggable={isLeadCard}
        onDragStart={isLeadCard ? () => onDragStart({ inquiryId: job.inquiryId!, boardStage: job.boardStage }) : undefined}
        onDragEnd={isLeadCard ? () => onDragStart(null) : undefined}
        onClick={goToJob}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            goToJob()
          }
        }}
        className={cn(
          'shrink-0 py-0',
          isLeadCard ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        )}
      >
        <div className="rounded-lg border p-3 text-left transition-colors hover:bg-muted/50">{body}</div>
      </Card>
    </PipelineBoardCardMenu>
  )
}

/** Right-click menu on a card, matching the Leads kanban's card menu. */
function PipelineBoardCardMenu({
  otherStage,
  onOpen,
  onMove,
  children,
}: {
  otherStage: { inquiryStage: InquiryStageEnum; label: string } | undefined
  onOpen: () => void
  onMove: (stage: InquiryStageEnum) => void
  children: ReactNode
}) {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={onOpen}>
          <FolderOpen />
          Open job
        </ContextMenuItem>
        {otherStage && (
          <ContextMenuItem onSelect={() => onMove(otherStage.inquiryStage)}>
            <ArrowRightLeft />
            {otherStage.label}
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  )
}
